import React, { useCallback, useEffect, useState } from 'react';
import { ColumnDef } from "@tanstack/react-table";
import useDataTable from 'hooks/useDataTable';
import { IRockQueryParam, Rocks } from 'models/rocks';
import IconButton from 'Components/atoms/buttons/icon-button';
import TableRowControlsContainer from 'Components/atoms/containers/table-row-controls-container';
import { createSelector } from 'reselect';
import { RocksSliceState } from "slices/business-development/rocks/reducer"
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import { deleteRock, getRocks, getRocksSearchFields } from 'slices/business-development/rocks/thunk';
import { HumanResourcesSliceState } from 'slices/human-resources/reducer';
import { getManagers } from 'slices/human-resources/thunk';
import Employee from 'models/employee';
import { QUARTERS, YEARS } from 'data/rocks-form';
import { navigateToCreateRocks, navigateToEditRocks } from 'utils/navigator';
import withRouter from 'Components/Common/withRouter';
import DataTableTemplate from 'Components/templates/data-table-template';
import ActionButton from 'Components/atoms/buttons/action-button';
import SearchRocksForm from 'Components/organisms/forms/search-rocks-form';
import Modal from 'Components/molecule/modal';
import AreYouSureButtons from 'Components/molecule/are-you-sure-buttons';
import useManual from "hooks/useManual"
import { PlusIcon, HelpIcon, PencilIcon, TrashIcon } from "Components/atoms/icons";


const selectRocks = createSelector(
    (state: any) => state.Rocks,
    ({ rocks, rocksCount, isLoadingRocks, rocksStatus, isDeletingRocks }: RocksSliceState) => ({
        rocks, rocksCount, isLoadingRocks, rocksStatus, isDeletingRocks
    })
)

const selectHumanResources = createSelector(
    (state: any) => state.HumanResources,
    ({ managers }: HumanResourcesSliceState) => ({
        managers
    })
)



const RocksListPage = (props: any) => {
    document.title = "Project ROCKS | IbexIQ";
    const dispatch: any = useDispatch();
    const [query, setQuery] = useState<IRockQueryParam>({
        page: 0,
        limit: Number.MAX_SAFE_INTEGER
    })
    const { handleOpenManual } = useManual("business-development/rocks");
    const [focusedRockId, setFocusedRockId] = useState<string | null>(null)
    const [loadedSearchFields, setLoadedSearchFields] = useState<boolean>(false)
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
    const {
        rocks,
        rocksCount,
        isLoadingRocks,
        rocksStatus,
        isDeletingRocks
    } = useSelector(selectRocks)

    const {
        managers
    } = useSelector(selectHumanResources)


    const initializePage = async () => {
        if (rocksStatus.length === 0 || managers.length === 0) {
            dispatch(getManagers())
            dispatch(getRocksSearchFields())
        }

        if (rocks.length === 0) {
            dispatch(getRocks(query))
        }

        setLoadedSearchFields(true)

    }

    useEffect(() => {
        initializePage();
    }, [])


    const getStatusColor = (status: string) => {
        if (status === "In Progress")
            return "blue"

        if (status === "Not Started")
            return "orange"

        return 'green'
    }


    const handleChangeQuery = <K extends keyof IRockQueryParam>(
        key: K,
        value: IRockQueryParam[K]
    ) => {

        if (key !== 'page' && key !== 'limit') {
            query.page = 0;
            query.limit = Number.MAX_SAFE_INTEGER
        }

        const q = {
            ...query,
            [key]: value
        }
        setQuery(q);
        dispatch(getRocks(q))

    };


    const handleClearSearch = useCallback(() => {
        const q = {
            page: 0,
            limit: Number.MAX_SAFE_INTEGER,
            quarter: '',
            year: '',
            accountableId: '',
            statusId: ''
        }
        setQuery(q)
        dispatch(getRocks(q))
    }, [setQuery, getRocks]);

    const handleEditRock = (rockId: string) => {
        navigateToEditRocks(props.router.navigate, rockId)
    }

    const onDeleteRock = (rockId: string) => {
        setIsModalOpen(true);
        setFocusedRockId(rockId)
    }

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setFocusedRockId(null);
    }

    const handleAddRock = () => {
        navigateToCreateRocks(props.router.navigate)
    }

    const handleDeleteRock = useCallback(async () => {
        if (focusedRockId) {
            const res = await dispatch(deleteRock(focusedRockId))
            res && handleCloseModal();
        }
    }, [focusedRockId, handleCloseModal])

    const columns = React.useMemo<ColumnDef<Rocks>[]>(() => [
        {
            header: "Quarter",
            id: "quarter",
            accessorFn: (row) => `${row.year} ${row.quarter}`,
            cell: ({ row }) => `${row.original.quarter} ${row.original.year}`
        },
        {
            header: "Status",
            id: "status",
            accessorFn: (row) => row.rocksStatus?.description,
            meta: {
                getHeaderClassName: () => 'text-end',
                getCellClassName: () => 'text-end',
            },
            cell: ({ row }) => (<span
                style={{
                    color: row.original.rocksStatus ? getStatusColor(row.original.rocksStatus.description) : undefined
                }}
            >{row.original.rocksStatus ? row.original.rocksStatus.description : 'Not found'}</span>),
        },
        {
            header: "ROCKS",
            id: "rocks",
            accessorFn: (row) => row.rocks?.trim(),
            meta: {
                getHeaderClassName: () => 'text-end',
                getCellClassName: () => 'text-end',
            },
            cell: ({ row }) => row.original.rocks ? (row.original.rocks.trim().length >= 50 ? (row.original.rocks.trim().slice(0, 50) + '...') : row.original.rocks.trim()) : "",
        },
        {
            header: "Accountable",
            id: "accountable",
            accessorFn: (row) => row.accountable ? `${row.accountable.commonName ?? row.accountable.firstName} ${row.accountable.lastName}` : undefined,
            meta: {
                getHeaderClassName: () => 'text-end',
                getCellClassName: () => 'text-end',
            },
            cell: ({ row }) => row.original?.accountable ? `${row.original.accountable.commonName ?? row.original.accountable.firstName} ${row.original.accountable.lastName}` : "Not found"
        },
        {
            header: "Help From",
            id: "helpFrom",
            accessorFn: (row) => row.helpFrom ?? undefined,
            meta: {
                getHeaderClassName: () => 'text-end',
                getCellClassName: () => 'text-end',
            },
            cell: ({ row }) =>
                row.original.helpFrom,
        },
        {
            header: "Date Of Completion",
            id: "dateOfCompletion",
            accessorFn: (row) => row.completedAt ? row.completedAt.toString() : undefined,
            meta: {
                getHeaderClassName: () => 'text-end',
                getCellClassName: () => 'text-end',
            },
            cell: ({ row }) =>
                row.original.completedAt ? row.original.completedAt.toString().split("T")[0] : 'None',
        },

        {
            header: "Comments",
            id: "description",
            accessorFn: (row) => row.description?.trim(),
            meta: {
                getHeaderClassName: () => 'text-end',
                getCellClassName: () => 'text-end',
            },
            cell: ({ row }) =>
                row.original.description ? (row.original.description.trim().length >= 50 ? (row.original.description.trim().slice(0, 50) + '...') : row.original.description.trim()) : "",
        },
        {
            header: "Controls",
            id: "controls",
            enableSorting: false,
            meta: {
                getHeaderClassName: () => 'text-end',
                getCellClassName: () => 'text-end',
            },
            cell: ({ row }) => <div className='d-flex flex-row justify-content-end'>
                <IconButton onClick={() => handleEditRock(row.original.id)}><PencilIcon /></IconButton>
                <IconButton onClick={() => onDeleteRock(row.original.id)}><TrashIcon /></IconButton>
            </div>
        }
    ], []);




    const table = useDataTable({
        columns,
        data: rocks
    })

    return <DataTableTemplate
        title={'Project Managment'}
        breadcrumbItem={'Rocks'}
        table={table}
        columns={columns}
        isLoading={isLoadingRocks}
        rowCount={rocksCount}
        actions={<>
            <ActionButton
                onClick={handleAddRock}
            >
                Add <PlusIcon />
            </ActionButton>
            {
                <ActionButton
                    onClick={handleOpenManual}
                >
                    Help <HelpIcon />
                </ActionButton>
            }
        </>
        }
        filters={<SearchRocksForm
            handleChangeQuery={handleChangeQuery}
            handleClearSearch={handleClearSearch}
            employees={[{ value: '', label: 'All' }, ...managers.map((emp: Employee) => ({ value: emp.id, label: `${emp.commonName ?? emp.firstName} ${emp.lastName}` }))]}
            status={[{ value: '', label: 'All' }, ...rocksStatus.map(y => ({ label: y.description, value: y.id }))]}
            years={[{ value: '', label: 'All' }, ...YEARS.map(y => ({ label: String(y), value: String(y) }))]}
            quarter={[{ value: '', label: 'All' }, ...QUARTERS.map(q => ({ label: q, value: q }))]}
            query={query}
        />}
        modal={
            <Modal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                title={'Delete rock'}
                children={
                    <AreYouSureButtons
                        disable={isDeletingRocks}
                        handleAccept={handleDeleteRock}
                        handleDecline={handleCloseModal}
                        acceptText={'Delete Rock'}
                        declineText={'Cancel'}
                    />
                }
            />
        }
    />
}

export default withRouter(RocksListPage);